/**
 * /akun/hapus-data — Hapus Data Saya (Phase 5.4).
 *
 * 2-step confirmation per doc 08 §8.3:
 *  - Step 1: warning screen with data loss preview
 *  - Step 2: confirmation modal requiring user to TYPE "HAPUS"
 *  - On confirm: cascade DELETE all data for this device_id
 *    (uses existing DELETE /v1/child/:id which cascades via FK ON DELETE
 *    CASCADE), then DELETE /v1/parent/pin to clear the gate
 *  - On success: navigate to /welcome (re-onboarding)
 *
 * UU PDP Pasal 5 (right to delete) + right to data portability
 * (CSV export is one button away on /akun/pengaturan).
 */

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  ActivityIndicator,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { listChildren, deleteChild } from '@/lib/api/children';
import { deletePin } from '@/lib/api/parent';
import { loadSession, clearSession } from '@/lib/parent-session';
import { ApiError } from '@/lib/api/client';

const CONFIRM_WORD = 'HAPUS';

type Phase = 'warning' | 'confirm' | 'deleting' | 'done' | 'error';

export default function HapusDataScreen() {
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>('warning');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ worksheets: number }>({ worksheets: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const session = await loadSession();
      if (!session) {
        router.replace('/akun/pin' as never);
        return;
      }
      // Fetch child to get summary.
      try {
        const children = await listChildren();
        setSummary({ worksheets: 0 }); // approximation; full count in Y2
        void children;
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [router]);

  const onConfirm = useCallback(async () => {
    if (confirmText !== CONFIRM_WORD) return;
    setPhase('deleting');
    setError(null);
    try {
      const session = await loadSession();
      // Delete all children (cascade wipes everything).
      const children = await listChildren();
      for (const child of children) {
        await deleteChild(child.id);
      }
      // Delete PIN (clear the gate).
      if (session) {
        try {
          await deletePin(session.token);
        } catch {
          // ignore — PIN may not exist
        }
      }
      // Clear local session.
      await clearSession();
      setPhase('done');
      // Brief delay for the success state, then navigate.
      setTimeout(() => {
        router.replace('/' as never);
      }, 1500);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Gagal menghapus data.';
      setError(message);
      setPhase('error');
    }
  }, [confirmText, router]);

  if (loading) {
    return (
      <ThemedView style={styles.root}>
        <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
          <View style={styles.center}>
            <ActivityIndicator size="large" color={Brand.primary} />
          </View>
        </SafeAreaView>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.back()}
            style={({ pressed }) => [styles.backBtn, pressed && styles.btnPressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <Text style={styles.backArrow}>←</Text>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Hapus Data Saya</ThemedText>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.warningCard}>
            <ThemedText type="title" style={styles.warningTitle}>
              Tindakan ini permanen
            </ThemedText>
            <ThemedText type="default" themeColor="textSecondary" style={styles.warningBody}>
              Semua data kamu dan anak akan dihapus permanen. Tidak bisa dibatalkan.
            </ThemedText>

            <View style={styles.dataList}>
              <DataLine>Profil anak</DataLine>
              <DataLine>Preferensi tema + aktivitas</DataLine>
              <DataLine>Aktivitas worksheet</DataLine>
              <DataLine>PIN Profil dan Preferensi</DataLine>
            </View>

            <ThemedText type="small" themeColor="textSecondary" style={styles.hint}>
              Tip: Export CSV dulu di Pengaturan kalau mau backup.
            </ThemedText>
          </View>

          {error ? (
            <View style={styles.errorCard}>
              <ThemedText type="smallBold" style={styles.errorTitle}>Gagal menghapus</ThemedText>
              <ThemedText type="default" themeColor="textSecondary">{error}</ThemedText>
            </View>
          ) : null}

          {phase === 'done' ? (
            <View style={styles.successCard}>
              <Text style={styles.successEmoji}>✓</Text>
              <ThemedText type="smallBold" style={styles.successTitle}>
                Data dihapus
              </ThemedText>
              <ThemedText type="default" themeColor="textSecondary">
                Mengalihkan ke onboarding...
              </ThemedText>
            </View>
          ) : (
            <Pressable
              onPress={() => setPhase('confirm')}
              disabled={phase === 'deleting'}
              style={({ pressed }) => [
                styles.deleteBtn,
                phase === 'deleting' && styles.btnDisabled,
                pressed && styles.btnPressed,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Lanjut ke konfirmasi hapus data"
            >
              <Text style={styles.deleteBtnText}>
                {phase === 'deleting' ? 'Menghapus…' : 'Hapus Data Saya'}
              </Text>
            </Pressable>
          )}
        </ScrollView>

        {/* Confirm modal — type HAPUS to confirm */}
        <Modal
          visible={phase === 'confirm'}
          transparent
          animationType="fade"
          onRequestClose={() => setPhase('warning')}
        >
          <View style={styles.modalBackdrop}>
            <View style={styles.modalCard}>
              <ThemedText type="smallBold" style={styles.modalTitle}>
                Ketik HAPUS untuk konfirmasi
              </ThemedText>
              <ThemedText type="small" themeColor="textSecondary" style={styles.modalBody}>
                Tindakan ini tidak bisa dibatalkan.
              </ThemedText>
              <TextInput
                value={confirmText}
                onChangeText={setConfirmText}
                placeholder={CONFIRM_WORD}
                placeholderTextColor="#9CA3AF"
                style={styles.confirmInput}
                autoCapitalize="characters"
                autoCorrect={false}
                maxLength={5}
                accessibilityLabel={`Ketik ${CONFIRM_WORD} untuk konfirmasi`}
              />
              <View style={styles.modalActions}>
                <Pressable
                  onPress={() => {
                    setConfirmText('');
                    setPhase('warning');
                  }}
                  style={({ pressed }) => [styles.btnSecondary, pressed && styles.btnPressed]}
                >
                  <Text style={styles.btnSecondaryText}>Batal</Text>
                </Pressable>
                <Pressable
                  onPress={onConfirm}
                  disabled={confirmText !== CONFIRM_WORD}
                  style={({ pressed }) => [
                    styles.btnDestructive,
                    confirmText !== CONFIRM_WORD && styles.btnDisabled,
                    pressed && styles.btnPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Konfirmasi hapus data"
                >
                  <Text style={styles.btnDestructiveText}>Hapus Permanen</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>
      </SafeAreaView>
    </ThemedView>
  );
}

function DataLine({ children }: { children: React.ReactNode }) {
  return (
    <View style={styles.dataLine}>
      <ThemedText type="default" style={styles.dataText}>{children}</ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
  },
  backBtn: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  backArrow: { fontSize: 24, color: Brand.primary, fontWeight: '600' },
  headerTitle: { flex: 1, textAlign: 'center', fontSize: 18, fontWeight: '700', color: '#1A0B5C' },
  scrollContent: { padding: Spacing.four, gap: Spacing.four, paddingBottom: Spacing.five },
  warningCard: {
    backgroundColor: '#fff7ed',
    borderWidth: 2,
    borderColor: '#ef4444',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    gap: Spacing.three,
  },
  warningTitle: { color: '#c2410c', textAlign: 'center' },
  warningBody: { textAlign: 'center' },
  dataList: { gap: Spacing.two, marginVertical: Spacing.two },
  dataLine: { flexDirection: 'row', alignItems: 'center', gap: Spacing.two },
  dataText: { color: '#1A0B5C' },
  hint: { textAlign: 'center', fontStyle: 'italic' },
  deleteBtn: {
    backgroundColor: '#ef4444',
    paddingVertical: 14,
    borderRadius: Radii.medium,
    alignItems: 'center',
  },
  deleteBtnText: { color: '#ffffff', fontSize: 16, fontWeight: '700' },
  btnPressed: { opacity: 0.7 },
  btnDisabled: { opacity: 0.5 },
  btnSecondary: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: Radii.medium,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    alignItems: 'center',
  },
  btnSecondaryText: { color: '#4b5563', fontSize: 14, fontWeight: '600' },
  btnDestructive: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: Radii.medium,
    backgroundColor: '#ef4444',
    alignItems: 'center',
  },
  btnDestructiveText: { color: '#ffffff', fontSize: 14, fontWeight: '700' },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.four,
  },
  modalCard: {
    backgroundColor: '#ffffff',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    width: '100%',
    maxWidth: 360,
    gap: Spacing.three,
  },
  modalTitle: { color: '#1A0B5C', textAlign: 'center' },
  modalBody: { textAlign: 'center' },
  confirmInput: {
    backgroundColor: '#f5f3ee',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 4,
    textAlign: 'center',
    color: '#1A0B5C',
  },
  modalActions: { flexDirection: 'row', gap: Spacing.two },
  errorCard: {
    backgroundColor: '#fee2e2',
    borderWidth: 1,
    borderColor: '#ef4444',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    alignItems: 'center',
    gap: Spacing.two,
  },
  errorTitle: { color: '#c2410c' },
  successCard: {
    backgroundColor: '#d1fae5',
    borderWidth: 1,
    borderColor: '#4caf50',
    borderRadius: Radii.medium,
    padding: Spacing.four,
    alignItems: 'center',
    gap: Spacing.two,
  },
  successEmoji: { fontSize: 32 },
  successTitle: { color: '#15803d' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
});
